// ─────────────────────────────────────────────────────────
// App.tsx — React Router v7 with all 40 routes
// Every module is lazy-loaded — only downloaded when visited.
// Auth guard redirects unauthenticated users to /login.
// ─────────────────────────────────────────────────────────
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import AppLayout      from '@/layouts/AppLayout'
import AuthLayout     from '@/layouts/AuthLayout'
import ErrorBoundary  from '@/components/ErrorBoundary'

// ── Lazy imports — each chunk downloaded only on first visit ──
const LoginPage         = lazy(() => import('@/modules/auth/LoginPage'))
const RegisterPage      = lazy(() => import('@/modules/auth/RegisterPage'))
const AcceptInvitePage  = lazy(() => import('@/modules/auth/AcceptInvitePage'))

// Overview
const DashboardPage       = lazy(() => import('@/modules/dashboard/DashboardPage'))
const ProjectsPage        = lazy(() => import('@/modules/projects/ProjectsPage'))
const ProjectDetailPage   = lazy(() => import('@/modules/projects/ProjectDetailPage'))
const BoqPage          = lazy(() => import('@/modules/boq/BoqPage'))
const MilestonesPage   = lazy(() => import('@/modules/milestones/MilestonesPage'))
const DailyPage        = lazy(() => import('@/modules/daily/DailyPage'))
const TasksPage        = lazy(() => import('@/modules/tasks/TasksPage'))

// Tenders & Billing
const TenderPage       = lazy(() => import('@/modules/tender/TenderPage'))
const EmdPage          = lazy(() => import('@/modules/emd/EmdPage'))
const ReceivablesPage  = lazy(() => import('@/modules/receivables/ReceivablesPage'))
const RetentionPage    = lazy(() => import('@/modules/retention/RetentionPage'))
const EinvoicePage     = lazy(() => import('@/modules/einvoice/EinvoicePage'))

// Finance
const ExpensesPage     = lazy(() => import('@/modules/expenses/ExpensesPage'))
const CashflowPage     = lazy(() => import('@/modules/cashflow/CashflowPage'))
const ProfitabilityPage= lazy(() => import('@/modules/profitability/ProfitabilityPage'))
const PettycashPage    = lazy(() => import('@/modules/pettycash/PettyCashPage'))
const BankreconPage    = lazy(() => import('@/modules/bankrecon/BankreconPage'))

// Procurement
const VendorsPage      = lazy(() => import('@/modules/vendors/VendorsPage'))
const PoPage           = lazy(() => import('@/modules/po/PoPage'))
const QuotationsPage   = lazy(() => import('@/modules/quotations/QuotationsPage'))
const InvoicesPage     = lazy(() => import('@/modules/invoices/InvoicesPage'))

// HR & Payroll
const EmployeesPage    = lazy(() => import('@/modules/employees/EmployeesPage'))
const AttendancePage   = lazy(() => import('@/modules/attendance/AttendancePage'))
const SalaryPage       = lazy(() => import('@/modules/salary/SalaryPage'))
const SlipsPage        = lazy(() => import('@/modules/slips/SlipsPage'))
const AdvancesPage     = lazy(() => import('@/modules/advances/AdvancesPage'))
const PfregPage        = lazy(() => import('@/modules/pfreg/PfregPage'))
const InsurancePage    = lazy(() => import('@/modules/insurance/InsurancePage'))

// Compliance
const TdsPage          = lazy(() => import('@/modules/tds/TdsPage'))
const AuditlogPage     = lazy(() => import('@/modules/auditlog/AuditlogPage'))

// Settings
const NotificationsPage= lazy(() => import('@/modules/notifications/NotificationsPage'))
const SettingsPage     = lazy(() => import('@/modules/settings/SettingsPage'))
const BrandingPage     = lazy(() => import('@/modules/branding/BrandingPage'))
const UsersPage        = lazy(() => import('@/modules/users/UsersPage'))
const BillingPage      = lazy(() => import('@/modules/billing/BillingPage'))
const BulkimportPage   = lazy(() => import('@/modules/bulkimport/BulkimportPage'))
const BulkExportPage   = lazy(() => import('@/modules/bulkexport/BulkExportPage'))
const ReportsPage      = lazy(() => import('@/modules/reports/ReportsPage'))

// Profile
const ProfilePage      = lazy(() => import('@/modules/profile/ProfilePage'))

// Super Admin
const AdminPage        = lazy(() => import('@/modules/admin/AdminPage'))
const AcCompaniesPage  = lazy(() => import('@/modules/admin/AcCompaniesPage'))
const AcOverviewPage   = lazy(() => import('@/modules/admin/AcOverviewPage'))
const AcSubscriptionsPage = lazy(() => import('@/modules/admin/AcSubscriptionsPage'))

// ── Page loading fallback ─────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '60px 24px', color: 'var(--color-tx3)',
    }}>
      <div style={{
        width: 20, height: 20, borderRadius: '50%',
        border: '2px solid var(--color-bd2)',
        borderTopColor: 'var(--color-ac)',
        animation: 'spin .7s linear infinite',
        marginRight: 10,
      }} />
      <span style={{ fontSize: 13 }}>Loading…</span>
    </div>
  )
}

// ── Auth guard ────────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()

  // Only block on initial Supabase auth resolution (user not yet known).
  // Do NOT block when user is authenticated but data is loading —
  // that causes AppLayout + useCompanyData to unmount, preventing
  // setLoading(false) from ever being called (infinite spinner loop).
  if (isLoading && !user) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-sf)', gap: 14,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: '3px solid var(--color-bd2)',
          borderTopColor: 'var(--color-ac)',
          animation: 'spin .7s linear infinite',
        }} />
        <span style={{ fontSize: 13, color: 'var(--color-tx3)', fontFamily: 'var(--font-body)' }}>
          Loading CivilBillDesk…
        </span>
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

// ── Super admin guard ─────────────────────────────────────
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { isPlatformAdmin } = useAuthStore()
  if (!isPlatformAdmin()) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// ── App ───────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* ── Auth routes (no sidebar) ──────────────── */}
          <Route element={<AuthLayout />}>
            <Route path="/login"          element={<LoginPage />} />
            <Route path="/register"       element={<RegisterPage />} />
            <Route path="/accept-invite"  element={<AcceptInvitePage />} />
          </Route>

          {/* ── App routes (with sidebar) ─────────────── */}
          <Route element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }>
            {/* Default redirect */}
            <Route index element={<Navigate to="/dashboard" replace />} />

            {/* Overview */}
            <Route path="/dashboard"       element={<DashboardPage />} />
            <Route path="/projects"        element={<ProjectsPage />} />
            <Route path="/projects/:id"    element={<ProjectDetailPage />} />
            <Route path="/boq"           element={<BoqPage />} />
            <Route path="/milestones"    element={<MilestonesPage />} />
            <Route path="/daily"         element={<DailyPage />} />
            <Route path="/planner"       element={<TasksPage />} />

            {/* Tenders & Billing */}
            <Route path="/tender"        element={<TenderPage />} />
            <Route path="/tender-cs"     element={<TenderPage />} />
            <Route path="/emd"           element={<EmdPage />} />
            <Route path="/receivables"   element={<ReceivablesPage />} />
            <Route path="/retention"     element={<RetentionPage />} />
            <Route path="/einvoice"      element={<EinvoicePage />} />

            {/* Finance */}
            <Route path="/expenses"      element={<ExpensesPage />} />
            <Route path="/cashflow"      element={<CashflowPage />} />
            <Route path="/profitability" element={<ProfitabilityPage />} />
            <Route path="/pettycash"     element={<PettycashPage />} />
            <Route path="/bankrecon"     element={<BankreconPage />} />

            {/* Procurement */}
            <Route path="/vendors"       element={<VendorsPage />} />
            <Route path="/po"            element={<PoPage />} />
            <Route path="/quotations"    element={<QuotationsPage />} />
            <Route path="/invoices"      element={<InvoicesPage />} />

            {/* HR & Payroll */}
            <Route path="/employees"     element={<EmployeesPage />} />
            <Route path="/attendance"    element={<AttendancePage />} />
            <Route path="/salary"        element={<SalaryPage />} />
            <Route path="/slips"         element={<SlipsPage />} />
            <Route path="/advances"      element={<AdvancesPage />} />
            <Route path="/pfreg"         element={<PfregPage />} />
            <Route path="/insurance"     element={<InsurancePage />} />

            {/* Compliance */}
            <Route path="/tds"           element={<TdsPage />} />
            <Route path="/auditlog"      element={<AuditlogPage />} />

            {/* Settings */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings"      element={<SettingsPage />} />
            <Route path="/branding"      element={<BrandingPage />} />
            <Route path="/users"         element={<UsersPage />} />
            <Route path="/billing"       element={<BillingPage />} />
            <Route path="/bulkimport"    element={<BulkimportPage />} />
            <Route path="/bulkexport"    element={<BulkExportPage />} />
            <Route path="/reports"       element={<ReportsPage />} />
            <Route path="/profile"       element={<ProfilePage />} />

            {/* Super Admin */}
            <Route path="/ac-companies" element={
              <RequireSuperAdmin><AcCompaniesPage /></RequireSuperAdmin>
            } />
            <Route path="/ac-overview" element={
              <RequireSuperAdmin><AcOverviewPage /></RequireSuperAdmin>
            } />
            <Route path="/ac-subscriptions" element={
              <RequireSuperAdmin><AcSubscriptionsPage /></RequireSuperAdmin>
            } />
            <Route path="/admin" element={
              <RequireSuperAdmin><AdminPage /></RequireSuperAdmin>
            } />
          </Route>

          {/* ── 404 catch-all ────────────────────────── */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />

        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
